import { useMemo } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/_module/components/ui/tooltip";
import {
  TrendingUp,
  TrendingDown,
  Users,
  Target,
  Phone,
  Info,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";

interface LeaderboardStats {
  totalPremium: number;
  issuedPremium?: number;
  totalSales: number;
  conversionRate: number;
  leadsConverted: number;
  appointments: number;
  callsMade: number;
  activeClients: number;
}

interface LeaderboardAgent {
  issuedPremium?: number;
  id: string | number;
  name: string;
  department: string;
  totalPremium: number;
  totalSales: number;
  conversionRate: number;
  leadsConverted: number;
  appointments: number;
  callsMade: number;
  activeClients: number;
  showInLeaderboard: boolean;
  avatar: string;
  rank: number;
}

interface PerformanceCardsProps {
  period: string;
  stats?: LeaderboardStats; // Real API stats
  agents?: LeaderboardAgent[]; // Real API agents data
}

export const PerformanceCards = function({
  period,
  stats,
  agents = []
}: PerformanceCardsProps) {
  // Extract primitive values from stats for stable dependencies
  const statsJson = stats ? JSON.stringify(stats) : null;

  const metrics = useMemo(() => {
    // Use real API stats if available, otherwise calculate from agents data
    if (stats) {
      return {
        totalSales: stats.totalSales,
        totalPremium: stats.totalPremium,
        issuedPremium: stats.issuedPremium ?? 0,
        avgConversion: stats.conversionRate,
        totalCalls: stats.callsMade,
        totalAppointments: stats.appointments,
        activeAgents: agents.filter(agent => agent.showInLeaderboard).length,
        totalAgents: agents.length,
        activeClients: stats.activeClients,
      };
    }

    // Fallback to calculating from agents data
    if (agents.length > 0) {
      const totalSales = agents.reduce((sum, agent) => sum + agent.totalSales, 0);
      const totalPremium = agents.reduce((sum, agent) => sum + agent.totalPremium, 0);
      const issuedPremium = agents.reduce((sum, agent) => sum + (agent.issuedPremium || 0), 0);
      // ⚠️ WEIGHTED, not a mean of the per-agent percentages.
      // The old `sum(conversionRate) / agents.length` gave every agent equal weight, so an
      // agent with ONE resolved lead that closed counted the same as one with 500 leads —
      // and agents with no resolved leads contributed a 0 that dragged it down. That is how
      // this headline came to read 100%. Summing the underlying counts gives the agency's
      // actual closing ratio, and it now matches the agency dashboard.
      const wonSum = agents.reduce((sum, a) => sum + ((a as any).wonLeads || 0), 0);
      const lostSum = agents.reduce((sum, a) => sum + ((a as any).lostLeads || 0), 0);
      const uwSum = agents.reduce((sum, a) => sum + ((a as any).underwritingLeads || 0), 0);
      const decisionBase = wonSum + lostSum + uwSum;
      const avgConversion = decisionBase > 0
        ? ((wonSum + uwSum) / decisionBase) * 100
        // Fall back to the old mean only if the backend hasn't sent the counts yet.
        : (agents.length > 0
            ? agents.reduce((sum, agent) => sum + agent.conversionRate, 0) / agents.length
            : 0);
      const totalCalls = agents.reduce((sum, agent) => sum + agent.callsMade, 0);
      const totalAppointments = agents.reduce((sum, agent) => sum + agent.appointments, 0);
      const activeAgents = agents.filter(agent => agent.showInLeaderboard).length;
      const activeClients = agents.reduce((sum, agent) => sum + agent.activeClients, 0);

      return {
        totalSales,
        totalPremium,
        issuedPremium,
        avgConversion,
        totalCalls,
        totalAppointments,
        activeAgents,
        totalAgents: agents.length,
        activeClients,
      };
    }

    // Default values if no data available
    return {
      totalSales: 0,
      totalPremium: 0,
      issuedPremium: 0,
      avgConversion: 0,
      totalCalls: 0,
      totalAppointments: 0,
      activeAgents: 0,
      totalAgents: 0,
      activeClients: 0,
    };
    // Use statsJson and agents for change detection
    // We need to include agents in the dependency array since we access it inside the callback
  }, [statsJson, agents]);

  // Trend data removed - needs historical data comparison implementation
  // For now, don't show trend data
  const getTrendData = (_metric: string) => {
    return null;
  };

  const cards = [
    {
      // Sold business leads — it is what agents are measured on. Production
      // (which also counts underwriting) sits beside it for context.
      title: "Total Premium Sold",
      value: `$${(metrics.issuedPremium || 0).toLocaleString()}`,
      icon: TrendingUp,
      color: "text-emerald-700",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200",
      hint: "Sold business only — everything marked with sold and approved strictly shows up here.",
    },
    {
      title: "Total Production",
      value: `$${metrics.totalPremium.toLocaleString()}`,
      icon: TrendingUp,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      hint: "Everything marked sold and approved as well as in underwriting.",
    },
    {
      title: "Closing Ratio",
      value: `${metrics.avgConversion.toFixed(1)}%`,
      hint: "Across the agents shown on this leaderboard. Sold and approved plus underwriting, out of leads that reached a decision — so it can differ from the agency dashboard, which covers every agent.",
      icon: Target,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
    },
    {
      title: "Total Calls Made",
      value: metrics.totalCalls.toLocaleString(),
      icon: Phone,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200",
    },
    {
      title: "Participating Agents",
      value: `${metrics.activeAgents}`,
      icon: Users,
      color: "text-indigo-600",
      bgColor: "bg-indigo-50",
      borderColor: "border-indigo-200",
    },
  ];

  return (
    <div className="space-y-4" data-tour="leaderboard-performance">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {cards.map((card, index) => (
          <Card
            key={index}
            className={`border-2 ${card.borderColor} hover:shadow-lg transition-all duration-200 hover:scale-105`}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="flex items-center gap-1 text-sm font-medium text-muted-foreground">
                {card.title}
                {!!(card as { hint?: string }).hint && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" aria-label="Info" className="inline-flex cursor-help items-center">
                          <Info className="h-3.5 w-3.5 text-primary/70 transition-colors hover:text-primary" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-64 text-xs">
                        {(card as { hint?: string }).hint}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </CardTitle>
              <div className={`p-2 rounded-lg ${card.bgColor}`}>
                <card.icon className={`h-4 w-4 ${card.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${card.color}`}>{card.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};